import { AlertTriangle, ArrowRight, CheckCircle2, GraduationCap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import LectureAiChip from './LectureAiChip';
import ModelBadge from './ModelBadge';

export default function MasteryJourneyPanel({ journey, subject }) {
  if (!journey) {
    return (
      <Card className="border-border/60 bg-card/70 shadow-card">
        <CardContent className="p-8 text-sm text-muted-foreground">
          No mastery journey yet. Refresh this subject to generate a strict, linear learning path.
        </CardContent>
      </Card>
    );
  }

  const phases = journey.phases || [];
  const blindspots = journey.blindspots || [];
  const nextActions = journey.next_actions || [];

  return (
    <Card className="border-border/60 bg-card/80 shadow-card">
      <CardHeader className="gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <LectureAiChip icon={GraduationCap} label={subject?.name || 'Subject'} tone="subject" active />
          {journey.updated_by_model ? <ModelBadge modelId={journey.updated_by_model} provider={journey.updated_by_model.startsWith('grok-') ? 'xai' : 'openai'} family={journey.updated_by_model === 'gpt-realtime' ? 'realtime' : 'reasoning'} /> : null}
        </div>
        <div>
          <CardTitle className="font-serif text-2xl">{journey.title}</CardTitle>
          {journey.summary ? <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">{journey.summary}</p> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4">
          {phases.map((phase, index) => (
            <div
              key={`${phase.title}-${index}`}
              className="rounded-[24px] border border-border/70 bg-background/80 p-5 transition-[transform,box-shadow] duration-200 ease-out motion-reduce:transition-none hover:-translate-y-[1px] hover:shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Phase {index + 1}</p>
                  <h3 className="mt-2 font-serif text-xl text-foreground">{phase.title}</h3>
                  {phase.goal ? <p className="mt-2 text-sm leading-6 text-muted-foreground">{phase.goal}</p> : null}
                </div>
                <ArrowRight className="mt-1 h-4 w-4 text-muted-foreground" />
              </div>

              {phase.required_evidence?.length ? (
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Required Evidence</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {phase.required_evidence.map((item) => (
                      <LectureAiChip key={item} label={item} tone="document" />
                    ))}
                  </div>
                </div>
              ) : null}

              {phase.checkpoints?.length ? (
                <div className="mt-4 space-y-2">
                  {phase.checkpoints.map((item) => (
                    <div key={item} className="flex items-start gap-2 text-sm text-foreground/85">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              {phase.pedantic_warning ? (
                <div className="mt-4 rounded-2xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4" />
                    <span>{phase.pedantic_warning}</span>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {(blindspots.length || nextActions.length) ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-[24px] border border-border/70 bg-background/80 p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Blindspots</p>
              <div className="mt-3 space-y-2">
                {blindspots.length ? blindspots.map((item) => (
                  <div key={item} className="text-sm text-foreground/80">{item}</div>
                )) : <div className="text-sm text-muted-foreground">No major blindspots surfaced yet.</div>}
              </div>
            </div>
            <div className="rounded-[24px] border border-border/70 bg-background/80 p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Next Actions</p>
              <div className="mt-3 space-y-2">
                {nextActions.length ? nextActions.map((item) => (
                  <div key={item} className="text-sm text-foreground/80">{item}</div>
                )) : <div className="text-sm text-muted-foreground">No follow-up actions yet.</div>}
              </div>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
